import {
  HttpError,
  useCreate,
  useGo,
  useInvalidate,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import { UploadFilesRef } from "components/uploader/UploadFiles/";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  BrandKit,
  BrandKitAsset,
  CreateBrandKitAssetRequest,
  PaletteResponse,
} from "../../types";
import { useForm } from "@refinedev/antd";
import { BrandKitAssetResponseWithTypedPurpose } from "../components/BrandKitAssets";
import { useOptimisticUpdate } from "hooks/useOptimisticUpdate/";
import { produce } from "immer";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics";
import { message } from "antd";
import { StagedBrandKitAsset } from "types/";
import { validateFiles } from "../helpers";
import { useHandleChangeFont } from "./useHandleChangeFont";
import { useCustomInvalidate } from "hooks/useCustomInvalidate/";
import { useEnsureBrandKitDefaultFonts } from "./useEnsureBrandKitDefaultFonts";
import { useSession } from "hooks/useSession/";

export const CREATE_ASSET_REQUEST_TYPE: CreateBrandKitAssetRequest["type"] =
  "CreateBrandKitAsset";

export type FontRefs = {
  Title: React.RefObject<UploadFilesRef>;
  Text: React.RefObject<UploadFilesRef>;
  Subtitles: React.RefObject<UploadFilesRef>;
};

export type VideoRefs = {
  Introduction: React.RefObject<UploadFilesRef>;
  Transition: React.RefObject<UploadFilesRef>;
  Background: React.RefObject<UploadFilesRef>;
  Closing: React.RefObject<UploadFilesRef>;
};

export type ImageRefs = {
  Logo: React.RefObject<UploadFilesRef>;
  Background: React.RefObject<UploadFilesRef>;
  Banner: React.RefObject<UploadFilesRef>;
};

export type MusicRefs = {
  Music: React.RefObject<UploadFilesRef>;
};

export type UploadRefs = {
  font: FontRefs;
  video: VideoRefs;
  image: ImageRefs;
  music: MusicRefs;
};

export function useBrandKitEdit(
  brandKitId?: string,
  organizationIdProp?: string
) {
  const uploadTitleFontRef = React.useRef<UploadFilesRef>(null);
  const uploadTextFontRef = React.useRef<UploadFilesRef>(null);
  const uploadSubtitlesFontRef = React.useRef<UploadFilesRef>(null);

  const uploadMusicRef = React.useRef<UploadFilesRef>(null);

  const uploadIntroductionVideoRef = React.useRef<UploadFilesRef>(null);
  const uploadTransitionVideoRef = React.useRef<UploadFilesRef>(null);
  const uploadBackgroundVideoRef = React.useRef<UploadFilesRef>(null);
  const uploadClosingVideoRef = React.useRef<UploadFilesRef>(null);

  const uploadLogoRef = React.useRef<UploadFilesRef>(null);
  const uploadBackgroundPictureRef = React.useRef<UploadFilesRef>(null);
  const uploadBannerRef = React.useRef<UploadFilesRef>(null);

  const fontRefs = useMemo(
    () => ({
      Title: uploadTitleFontRef,
      Text: uploadTextFontRef,
      Subtitles: uploadSubtitlesFontRef,
    }),
    []
  );

  const videoRefs = useMemo(
    () => ({
      Introduction: uploadIntroductionVideoRef,
      Transition: uploadTransitionVideoRef,
      Background: uploadBackgroundVideoRef,
      Closing: uploadClosingVideoRef,
    }),
    []
  );

  const imageRefs = useMemo(
    () => ({
      Logo: uploadLogoRef,
      Background: uploadBackgroundPictureRef,
      Banner: uploadBannerRef,
    }),
    []
  );

  const musicRefs = useMemo(
    () => ({
      Music: uploadMusicRef,
    }),
    []
  );

  // Keep the array for backwards compatibility with BrandKitAssets
  const refs = useMemo(
    () => [
      uploadMusicRef,
      uploadTitleFontRef,
      uploadTextFontRef,
      uploadSubtitlesFontRef,
      uploadIntroductionVideoRef,
      uploadTransitionVideoRef,
      uploadBackgroundVideoRef,
      uploadClosingVideoRef,
      uploadLogoRef,
      uploadBackgroundPictureRef,
      uploadBannerRef,
    ],
    []
  );

  const { mutateAsync: updateBrandKit } = useUpdate<BrandKit>();
  const { mutateAsync: createBrandKit, isPending: isCreatingBrandKit } =
    useCreate<BrandKit>();
  const [stagedAssets, setStagedAssets] = useState<StagedBrandKitAsset[]>([]);

  const { trackEvent } = useAnalytics();
  const go = useGo();
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { customInvalidate } = useCustomInvalidate();

  const { handleChangeFont } = useHandleChangeFont();
  const { ensureDefaultFonts } = useEnsureBrandKitDefaultFonts();
  const { currentOrganization: organization } = useSession();
  const organizationId = organizationIdProp ?? organization?.id;

  const { formProps, form, saveButtonProps, query, formLoading } = useForm<
    BrandKit,
    HttpError,
    PaletteResponse
  >({
    resource: `media/${organizationId}/brand_kits`,
    id: brandKitId,
    action: "edit",
    queryOptions: {
      enabled: Boolean(organizationId && brandKitId),
      queryKey: ["brandkit", brandKitId],
    },
    successNotification: false,
  });

  const [saving, setSaving] = useState(false);
  // Separate from `saving`: kept independent so one flow finishing doesn't
  // clear the flag while the other is still in progress.
  const [isDeletingFont, setIsDeletingFont] = useState(false);
  const brandKit = query?.data?.data;
  const brandkitAssets = useMemo(
    () =>
      brandKit?.assets as BrandKitAssetResponseWithTypedPurpose[] | undefined,
    [brandKit]
  );

  const optimisticRemoveAsset = useOptimisticUpdate<{ data: BrandKit }>(
    ["brandkit", brandKitId],
    useCallback(
      (_oldData, assetId: string) => {
        const result = produce(brandKit, (draft) => {
          if (draft && Array.isArray(draft.assets)) {
            draft.assets = draft.assets.filter(
              (asset) => asset.asset_id !== assetId
            );
          }
        });
        return { data: result };
      },
      [brandKit]
    )
  );

  const handleCreateBrandKit = async (
    values: { name: string; primaryColor: string },
    onSuccess?: () => void
  ): Promise<BrandKit | undefined> => {
    try {
      const response = await createBrandKit({
        resource: `media/${organizationId}/brand_kits`,
        values: {
          name: values.name,
          palette: {
            primary_color: values.primaryColor,
          },
        },
      });
      trackEvent(AnalyticsEvents.BrandKitCreate, {
        brandkit_id: response?.data.id,
      });
      if (response.data) {
        if (onSuccess) {
          onSuccess();
        } else {
          go({
            to: {
              id: response.data.id,
              resource: "media_brandkits",
              action: "edit",
            },
            query: { from: "create" },
          });
        }
        return response.data;
      } else {
        throw new Error(t("media.brandkit.create.failedToCreate"));
      }
    } catch (error) {
      message.error(t("media.brandkit.create.failedToCreate"));
      console.error(error);
      return undefined;
    }
  };

  const fontsToChange = useMemo(
    () =>
      stagedAssets.filter(
        (asset) =>
          asset.purpose &&
          ["Title", "Text", "Subtitles"].includes(asset.purpose) &&
          // uid not empty for custom uploaded fonts, empty for public fonts
          asset.uid.length === 0
      ),
    [stagedAssets]
  );

  const clearStagedFontForPurpose = useCallback(
    (purpose: string) => {
      const fontPurposes = ["Title", "Text", "Subtitles"];
      if (!fontPurposes.includes(purpose)) return;

      setStagedAssets((prev) => prev.filter((a) => a.purpose !== purpose));
      const ref = fontRefs[purpose as keyof FontRefs];
      ref?.current?.reset();
    },
    [setStagedAssets, fontRefs]
  );

  const brandKitFonts = useMemo(
    () =>
      brandKit?.assets?.filter(
        (asset) =>
          asset.purpose === "Title" ||
          asset.purpose === "Subtitles" ||
          asset.purpose === "Text"
      ) ?? [],
    [brandKit?.assets]
  );

  const [fonts, setFonts] = useState<BrandKitAsset[]>(brandKitFonts);

  useEffect(() => {
    setFonts(brandKitFonts);
  }, [brandKitFonts]);

  const updateFontStateOptimistically = useCallback(
    (
      purpose: string,
      update: { asset_id: string; description?: string; path?: string } | null
    ) => {
      setFonts(
        produce((draft) => {
          const index = draft.findIndex((f) => f.purpose === purpose);
          if (update === null) {
            if (index !== -1) {
              draft.splice(index, 1);
            }
            return;
          }
          if (index !== -1) {
            draft[index].asset_id = update.asset_id;
            draft[index].name = update.description ?? "";
            draft[index].path = update.path ?? "";
          }
        })
      );
    },
    []
  );

  const handleFontSelect = useCallback(
    (purpose: "Title" | "Text" | "Subtitles", fontId: string) => {
      clearStagedFontForPurpose(purpose);
      setStagedAssets((prev) => [
        ...prev,
        {
          asset_id: fontId,
          purpose,
          uid: "",
        },
      ]);
    },
    [clearStagedFontForPurpose, setStagedAssets]
  );

  const setDefaultFonts = useCallback(
    async (removedAssetId?: string) => {
      if (!brandKitId) return;

      // On delete, backend auto-assigns default fonts
      // Invalidate query to get fresh data with correct font paths
      if (removedAssetId) {
        setIsDeletingFont(true);
        try {
          await customInvalidate(["brandkit", brandKitId]);
        } finally {
          setIsDeletingFont(false);
        }
        return;
      }

      const currentFonts = brandKitFonts;
      const newFonts = await ensureDefaultFonts(brandKitId, organizationId, {
        Title: currentFonts.find((f) => f.purpose === "Title"),
        Text: currentFonts.find((f) => f.purpose === "Text"),
        Subtitles: currentFonts.find((f) => f.purpose === "Subtitles"),
      });

      if (newFonts.length) {
        setFonts([...currentFonts, ...newFonts]);
      }
    },
    [
      brandKitId,
      brandKitFonts,
      organizationId,
      ensureDefaultFonts,
      customInvalidate,
    ]
  );

  const saveDefaultFonts = useCallback(async () => {
    const fontChangePromises = fontsToChange.map((asset) => {
      if (!asset.purpose || !asset.asset_id) return Promise.resolve();
      return handleChangeFont(
        asset.purpose,
        asset.asset_id,
        organizationId,
        brandKitId
      );
    });

    await Promise.all(fontChangePromises);
  }, [fontsToChange, handleChangeFont, organizationId, brandKitId]);

  const handleEditSave = useCallback(
    async (
      value: { name: string; colors: PaletteResponse },
      onSuccess?: () => void
    ) => {
      trackEvent(AnalyticsEvents.BrandKitSave, {
        brandkit_id: brandKitId,
      });

      try {
        if (!validateFiles(stagedAssets)) {
          throw new Error(t("media.brandkit.edit.pleaseSetA"));
        }
        setSaving(true);

        if (stagedAssets) await saveDefaultFonts();

        // Assets themselves are uploaded immediately by AssetUploader on drop,
        // not here — this only persists name/colors and font-purpose changes.
        await updateBrandKit({
          resource: `media/${organizationId}/brand_kits`,
          id: brandKitId,
          values: {
            name: value.name,
            palette: value.colors,
          },
          invalidates: [],
        });

        await customInvalidate(["brandkit", brandKitId]);
        await invalidate({
          resource: `media/${organizationId}/brand_kits`,
          invalidates: ["list"],
        });
        setStagedAssets([]);

        refs.forEach((ref) => ref.current?.reset());

        if (onSuccess) {
          onSuccess();
        } else {
          go({
            to: {
              resource: "media_brandkits",
              id: brandKitId as string,
              action: "edit",
            },
          });
        }
      } catch (error) {
        console.error("Error saving brandkit", error);
        message.error(
          (
            error as {
              message: string;
            }
          )?.message ?? t("media.brandkit.edit.failedToSave")
        );
        throw error;
      } finally {
        setSaving(false);
      }
    },

    [
      brandKitId,
      setSaving,
      trackEvent,
      stagedAssets,
      setStagedAssets,
      refs,
      saveDefaultFonts,
      updateBrandKit,
      organizationId,
      go,
      t,
      customInvalidate,
      invalidate,
    ]
  );

  const isBrandKitLoading = useMemo(() => {
    return (
      (formLoading && !saving && !isDeletingFont) ||
      !brandKit ||
      !brandkitAssets
    );
  }, [formLoading, saving, isDeletingFont, brandKit, brandkitAssets]);

  return {
    form,
    formLoading,
    formProps,
    saveButtonProps,
    // Named refs for specific use cases
    fontRefs,
    videoRefs,
    imageRefs,
    musicRefs,
    // Array of all refs for backwards compatibility
    refs,
    query,
    saving,
    setSaving,
    brandKit,
    brandkitAssets,
    optimisticRemoveAsset,
    handleCreateBrandKit,
    isCreatingBrandKit,
    handleEditSave,
    stagedAssets,
    setStagedAssets,
    isBrandKitLoading,
    handleChangeFont,
    clearStagedFontForPurpose,
    setDefaultFonts,
    brandKitFonts,
    handleFontSelect,
    // Shared local fonts state for optimistic UI updates
    fonts,
    updateFontStateOptimistically,
  };
}
